import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { InitiativeSection } from '../initiatives/InitiativeSection';
import { CognocracyCredential } from './CognocracyCredential';
import { pillarHref, pillars } from '../../data/pillars';
import { fadeUp } from '../../utils/motion';
type FrameworkPartProps = {
  label: string;
  title: string;
  children: React.ReactNode;
};

/** How the framework works: assessment, development and recognition. */
export function CognocracyFramework() {
  return <InitiativeSection id="framework" title="How the framework works" intro={['Cognocracy combines three parts: an assessment model for demonstrated judgment, continuous development between assessments, and a way to recognize what people have shown.']} data-id="element-228" data-mp="298pwmzl2nlob">
      <div className="border-t border-line" data-id="element-229" data-mp="2n90mbzanau7w">
        <FrameworkPart label="Assessment" title="The NCJI" data-id="element-230" data-mp="2s0ilqix0smqr">
          <p className="text-lg text-ink-dark leading-relaxed" data-id="element-231" data-mp="2s23fw7ffa3o3">
            The Neurocognitive Civic Judgment Index is the proposed assessment model at the center of Cognocracy. It is
            intended to examine demonstrated judgment and readiness for responsibility within defined conditions.
          </p>
          <ol className="mt-6 border-t border-line" aria-label="NCJI’s five pillars" data-id="element-232" data-mp="24s15xr984gen">
            {pillars.map(pillar => <li key={pillar.id} className="grid grid-cols-[2.5rem_1fr] gap-4 py-5 border-b border-line" data-id="element-233" data-mp="2drefejxcl4zd">
                <span aria-hidden="true" className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-teal/60 text-sm font-semibold text-teal" data-id="element-234" data-mp="28sjxrlb49mgu">
                  {pillar.order}
                </span>
                <div data-id="element-235" data-mp="2ion1uwpj3v6f">
                  <a href={pillarHref(pillar.id)} className="font-heading font-semibold text-lg text-ink-dark hover:text-navy transition-colors duration-150" data-id="element-236" data-mp="tb9uszq17t1m">
                    {pillar.name}
                  </a>
                  <p className="mt-1 text-ink-muted leading-relaxed" data-id="element-237" data-mp="1e2yiot1ricxk">{pillar.description}</p>
                </div>
              </li>)}
          </ol>
          <p className="mt-6 border-l-2 border-teal pl-4 text-ink-muted leading-relaxed" data-id="element-238" data-mp="1n0io8e6nqvdz">
            These areas describe the proposed model. Assessment validity, scoring methods, and predictive usefulness
            require research and evaluation.
          </p>
          <a href="/ncji" className="mt-6 inline-flex items-center gap-2 font-semibold text-navy hover:text-navy-light transition-colors duration-150 sm:whitespace-nowrap" data-id="element-239" data-mp="210jmrmzg9qqa">
            About NCJI in the Human Capability Framework <ArrowRight size={16} aria-hidden="true" data-id="element-240" data-mp="2aq34gpbffejk" />
          </a>
        </FrameworkPart>

        <FrameworkPart label="Development" title="Continuous development" data-id="element-241" data-mp="2fripwihinn2h">
          <p className="text-lg text-ink-dark leading-relaxed" data-id="element-242" data-mp="2kq3t8qs2emv5">
            Participants could receive development roadmaps combining evidence-evaluation exercises, decision
            simulations, emotional regulation practice, and leadership preparation. Periodic reassessment would track
            change and identify further development needs.
          </p>
          <p className="mt-5 text-ink-muted leading-relaxed" data-id="element-243" data-mp="3qa2ek9egay43">
            Declining performance would be interpreted within its context and paired with a clear reassessment pathway.
            Mandatory participation or restrictions on access would require explicit rules and review.
          </p>
        </FrameworkPart>

        <FrameworkPart label="Recognition" title="Cognitive Social Capital and credentials" data-id="element-244" data-mp="i1lp8z1yemyu">
          <p className="text-lg text-ink-dark leading-relaxed" data-id="element-245" data-mp="d42ck3kwmi90">
            Cognitive Social Capital describes the reputational and practical value people could build through
            demonstrated reasoning, self-regulation, and responsible action. Cognocracy explores how these capabilities
            might become recognizable signals of readiness for professional and institutional responsibility.
          </p>
          <p className="mt-5 mb-8 text-ink-muted leading-relaxed" data-id="element-246" data-mp="v9szx5pmvrlr">
            A proposed Universal Capability Credential would communicate which capabilities were assessed, the
            standards met, when assessment occurred, and where the results are relevant.
          </p>
          <CognocracyCredential data-id="element-247" data-mp="19065x2iysw8g" />
        </FrameworkPart>
      </div>
    </InitiativeSection>;
}
function FrameworkPart({
  label,
  title,
  children
}: FrameworkPartProps) {
  return <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-16 py-12 border-b border-line" data-id="element-248" data-mp="zawcnjcdx7ir">
      <div className="lg:col-span-5" data-id="element-249" data-mp="148xw45dq38ur">
        <p className="text-sm font-medium text-teal mb-2" data-id="element-250" data-mp="147wne7k6iyg7">{label}</p>
        <h3 className="font-heading font-bold text-2xl text-ink-dark" data-id="element-251" data-mp="1ry915z23vpep">{title}</h3>
      </div>
      <div className="lg:col-span-7" data-id="element-252" data-mp="1wqadtm36ri59">{children}</div>
    </motion.div>;
}